/**
 * First-touch marketing attribution read off the landing URL.
 *
 * The name is historical: this started as Google Ads only, but the blob now also carries the Meta
 * paid funnel (humeo.de/auth-for-doctors). It ships to the backend as `googleAdsMetadata` and lands
 * in the user's `google_ads_metadata` JSON field, so the keys below are the keys analysts query —
 * `google_ads_metadata->>'ad_id'`. Keep them 1:1 with the URL params the ad platform sends.
 */

const GOOGLE_ADS_PARAMS = ['gad_source', 'gad_campaignid', 'gbraid', 'gclid'];

/** Meta paid funnel. Mirrored 1:1 in the ad URL params — no platform prefix. */
const META_ADS_PARAMS = ['site_source', 'fbclid', 'ad_id', 'adset_id', 'campaign_id', 'placement'];

const UTM_PREFIX = 'utm_';

/** Stripe caps metadata values at 500 chars; staying well under it also drops obvious junk. */
const MAX_VALUE_LENGTH = 255;

/**
 * Meta passes `{{ad.id}}` through untouched when the macro is not configured on the ad. Storing the
 * literal would look like real data and silently merge unrelated ads into one attribution bucket.
 */
const isUnexpandedMacro = (value: string): boolean =>
  value.startsWith('{{') && value.endsWith('}}');

/**
 * `searchParams.entries()` has already decoded once, so a bare `%` in a marketer-authored value
 * (ad names, utm_content) makes a second decode throw. Capture runs in an effect on the paid
 * landing page — a throw there would break the page for exactly the traffic we are measuring.
 */
const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export function extractGoogleAdsParams(searchParams: URLSearchParams): Record<string, string> {
  const result: Record<string, string> = {};

  for (const [key, value] of searchParams.entries()) {
    const shouldInclude =
      GOOGLE_ADS_PARAMS.includes(key) || META_ADS_PARAMS.includes(key) || key.startsWith(UTM_PREFIX);

    if (!shouldInclude || !value || result[key]) {
      continue;
    }

    const decoded = safeDecode(value);
    if (isUnexpandedMacro(decoded) || decoded.length > MAX_VALUE_LENGTH) {
      continue;
    }

    result[key] = decoded;
  }

  return result;
}

export const GOOGLE_ADS_METADATA_KEY = 'GOOGLE_ADS_METADATA';
